<template>
    <div v-if="appInstanceId && appInstanceId !== emptyGuid" class="journey-set-export-section">
        <div class="form-section-header">
            <p>Export Content for Migration</p>
        </div>
        <div class="export-toolbar custom-leftright-padding">
            <button type="button" class="export-toolbar-button button-outline" @click="selectAll">
                Select All
            </button>
            <button type="button" class="export-toolbar-button button-outline" @click="selectNone">
                Select None
            </button>
            <button
                type="button"
                class="export-toolbar-button button-outline"
                :disabled="selectedCount === 0 || auditing || exporting"
                @click="auditSelected">
                Audit Selected
            </button>
            <button
                type="button"
                class="export-toolbar-button button-primary"
                :disabled="selectedCount === 0 || auditing || exporting"
                @click="exportSelected">
                Export Selected
            </button>
        </div>
        <div
            v-if="auditReport || exportResult"
            class="export-actions custom-leftright-padding">
            <button
                v-if="auditReport"
                type="button"
                class="export-toolbar-button button-outline"
                @click="downloadAuditReport">
                Download Audit
            </button>
            <button
                v-if="exportResult"
                type="button"
                class="export-toolbar-button button-outline"
                @click="downloadExportResults">
                Download Export Results
            </button>
        </div>
        <p v-if="loaded && totalJourneySetCount === 0" class="custom-leftright-padding empty-label">
            No journey sets yet.
        </p>
        <template v-for="group in categoryGroups" :key="group.typeId">
            <div v-if="group.journeySets.length > 0" class="form-section-header category-header">
                <p>{{ group.categoryName }}</p>
            </div>
            <ion-item
                v-for="journeySet in group.journeySets"
                :key="journeySet.id"
                lines="full"
                mode="md">
                <ion-checkbox
                    slot="start"
                    :model-value="isSelected(journeySet.id)"
                    @update:model-value="setSelection(journeySet.id, $event)"
                    mode="md"
                    color="primary" />
                <ion-label>
                    <h2>{{ journeySet.title }}</h2>
                    <p v-if="journeySet.templateName">{{ journeySet.templateName }}</p>
                    <p v-if="getAuditStatusLabel(journeySet.id)" class="status-line">
                        <ion-icon
                            :icon="getAuditStatusIcon(journeySet.id)"
                            :class="getAuditStatusClass(journeySet.id)"
                            class="status-icon" />
                        <span :class="getAuditStatusClass(journeySet.id)">{{ getAuditStatusLabel(journeySet.id) }}</span>
                        <span v-if="getExportStatusLabel(journeySet.id)" :class="getExportStatusClass(journeySet.id)">
                            · {{ getExportStatusLabel(journeySet.id) }}
                        </span>
                    </p>
                </ion-label>
                <div slot="end" class="row-actions">
                    <button
                        v-if="hasAuditEntry(journeySet.id)"
                        type="button"
                        class="export-row-button button-outline"
                        @click.stop="viewAuditDetails(journeySet.id)">
                        View
                    </button>
                    <button
                        v-if="hasExportItem(journeySet.id)"
                        type="button"
                        class="export-row-button button-outline"
                        @click.stop="viewExportDetails(journeySet.id)">
                        Export Result
                    </button>
                </div>
            </ion-item>
        </template>

        <ion-modal :is-open="detailsModalOpen" @did-dismiss="closeDetailsModal">
            <ion-header>
                <ion-toolbar>
                    <ion-title>{{ detailsModalTitle }}</ion-title>
                    <ion-buttons slot="end">
                        <ion-button @click="closeDetailsModal">OK</ion-button>
                    </ion-buttons>
                </ion-toolbar>
            </ion-header>
            <ion-content class="ion-padding">
                <pre class="details-json">{{ detailsModalJson }}</pre>
            </ion-content>
        </ion-modal>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import {
    IonButton,
    IonButtons,
    IonCheckbox,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonModal,
    IonTitle,
    IonToolbar,
} from '@ionic/vue';
import { checkmarkCircle, closeCircle } from 'ionicons/icons';
import { execute } from '@/mixins/LoadingMixin';
import { getJourneySets } from '@/services/JourneySetService';
import {
    auditSelectedJourneySets,
    exportSelectedJourneySets,
    V1ExportAuditReport,
    V1ExportBatchResult,
    V1LessonSetAuditEntry,
    V1LessonSetExportItemResult,
} from '@/services/MigrationExportService';
import { JOURNEY_SET_TYPES, JourneySetTypeId } from '@/models/JourneySetTypes';
import { JourneySet } from '@/models/JourneySetModel';
import { EMPTY_GUID } from '@/utils/GuidUtil';
import { presentNativeAlert, presentValidationAlert } from '@/services/Notify';
import { downloadFile } from '@/utils/DownloadUtil';

interface CategoryGroup {
    typeId: string;
    categoryName: string;
    journeySets: JourneySet[];
}

export default defineComponent({
    name: 'JourneySetExportSection',
    components: {
        IonButton,
        IonButtons,
        IonCheckbox,
        IonContent,
        IonHeader,
        IonIcon,
        IonItem,
        IonLabel,
        IonModal,
        IonTitle,
        IonToolbar,
    },
    props: {
        appInstanceId: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const loaded = ref(false);
        const auditing = ref(false);
        const exporting = ref(false);
        const categoryGroups = ref<CategoryGroup[]>([]);
        const selectedIds = ref<Record<string, boolean>>({});
        const auditReport = ref<V1ExportAuditReport | null>(null);
        const exportResult = ref<V1ExportBatchResult | null>(null);
        const detailsModalOpen = ref(false);
        const detailsModalTitle = ref('');
        const detailsModalJson = ref('');

        const loadJourneySets = async () => {
            if (!props.appInstanceId || props.appInstanceId === EMPTY_GUID) {
                categoryGroups.value = [];
                selectedIds.value = {};
                auditReport.value = null;
                exportResult.value = null;
                loaded.value = true;
                return;
            }

            await execute(async () => {
                const quadrantKeys = Object.keys(JourneySetTypeId) as Array<keyof typeof JourneySetTypeId>;
                const groups: CategoryGroup[] = [];

                for (const quadrantKey of quadrantKeys) {
                    const typeId = JourneySetTypeId[quadrantKey];
                    const journeySets = await getJourneySets(typeId, props.appInstanceId, false);
                    groups.push({
                        typeId,
                        categoryName: JOURNEY_SET_TYPES.get(typeId) || quadrantKey,
                        journeySets: journeySets.sort((a, b) => a.displayOrder - b.displayOrder),
                    });
                }

                categoryGroups.value = groups;
                selectedIds.value = {};
                auditReport.value = null;
                exportResult.value = null;
                loaded.value = true;
            }, 'Error getting journey sets.');
        };

        watch(
            () => props.appInstanceId,
            () => {
                loaded.value = false;
                loadJourneySets();
            },
            { immediate: true }
        );

        const allJourneySetIds = computed(() =>
            categoryGroups.value.flatMap((group) => group.journeySets.map((js) => js.id))
        );

        const totalJourneySetCount = computed(() => allJourneySetIds.value.length);

        const selectedCount = computed(() =>
            allJourneySetIds.value.filter((id) => selectedIds.value[id]).length
        );

        const selectedJourneySetIds = () =>
            allJourneySetIds.value.filter((id) => selectedIds.value[id]);

        const auditById = computed(() => {
            const map: Record<string, V1LessonSetAuditEntry> = {};
            auditReport.value?.entries?.forEach((entry) => {
                map[entry.journeySetId] = entry;
            });
            return map;
        });

        const exportById = computed(() => {
            const map: Record<string, V1LessonSetExportItemResult> = {};
            exportResult.value?.lessonSets?.forEach((entry) => {
                map[entry.journeySetId] = entry;
            });
            return map;
        });

        const isSelected = (id: string) => Boolean(selectedIds.value[id]);

        const setSelection = (id: string, checked: boolean) => {
            selectedIds.value = {
                ...selectedIds.value,
                [id]: checked,
            };
        };

        const selectAll = () => {
            const next: Record<string, boolean> = {};
            allJourneySetIds.value.forEach((id) => {
                next[id] = true;
            });
            selectedIds.value = next;
        };

        const selectNone = () => {
            selectedIds.value = {};
        };

        const hasAuditEntry = (id: string) => Boolean(auditById.value[id]);
        const hasExportItem = (id: string) => Boolean(exportById.value[id]);

        const getAuditStatusLabel = (id: string) => {
            const entry = auditById.value[id];
            if (!entry) return '';
            return entry.success ? 'Passed' : 'Failed';
        };

        const getAuditStatusIcon = (id: string) => {
            const entry = auditById.value[id];
            if (!entry) return checkmarkCircle;
            return entry.success ? checkmarkCircle : closeCircle;
        };

        const getAuditStatusClass = (id: string) => {
            const entry = auditById.value[id];
            if (!entry) return '';
            return entry.success ? 'status-pass' : 'status-fail';
        };

        const getExportStatusLabel = (id: string) => {
            const item = exportById.value[id];
            if (!item) return '';
            if (item.success) return 'Exported';
            if (item.error?.includes('audit')) return 'Skipped (audit failed)';
            return 'Export failed';
        };

        const getExportStatusClass = (id: string) => {
            const item = exportById.value[id];
            if (!item) return '';
            if (item.success) return 'status-pass';
            if (item.error?.includes('audit')) return 'status-warn';
            return 'status-fail';
        };

        const openDetailsModal = (title: string, payload: unknown) => {
            detailsModalTitle.value = title;
            detailsModalJson.value = JSON.stringify(payload, null, 2);
            detailsModalOpen.value = true;
        };

        const closeDetailsModal = () => {
            detailsModalOpen.value = false;
            detailsModalTitle.value = '';
            detailsModalJson.value = '';
        };

        const viewAuditDetails = (id: string) => {
            const entry = auditById.value[id];
            if (!entry) return;
            openDetailsModal('Audit Details', entry);
        };

        const viewExportDetails = (id: string) => {
            const item = exportById.value[id];
            if (!item) return;
            openDetailsModal('Export Result', item);
        };

        const downloadJson = (filename: string, payload: unknown) => {
            downloadFile(JSON.stringify(payload, null, 2), filename);
        };

        const downloadAuditReport = () => {
            if (!auditReport.value) return;
            downloadJson(`audit-report-${props.appInstanceId}.json`, auditReport.value);
        };

        const downloadExportResults = () => {
            if (!exportResult.value) return;
            downloadJson(`export-results-${props.appInstanceId}.json`, exportResult.value);
        };

        const auditSelected = async () => {
            const ids = selectedJourneySetIds();
            if (ids.length === 0) {
                await presentValidationAlert('Please select at least one journey set to audit.');
                return;
            }

            auditing.value = true;
            try {
                await execute(async () => {
                    auditReport.value = await auditSelectedJourneySets(props.appInstanceId, ids);
                    const failed = auditReport.value.entries.filter((e) => !e.success).length;
                    const passed = auditReport.value.entries.length - failed;
                    await presentNativeAlert({
                        cssClass: 'custom-alert',
                        header: 'Export Content for Migration',
                        message: `Audit completed.\n\nPassed: ${passed}\nFailed: ${failed}`,
                        buttons: ['OK'],
                    });
                }, 'Error auditing content.');
            } finally {
                auditing.value = false;
            }
        };

        const exportSelected = async () => {
            const ids = selectedJourneySetIds();
            if (ids.length === 0) {
                await presentValidationAlert('Please select at least one journey set to export.');
                return;
            }

            exporting.value = true;
            try {
                await execute(async () => {
                    const result = await exportSelectedJourneySets(props.appInstanceId, ids);
                    exportResult.value = result;
                    if (result.auditReport) {
                        auditReport.value = result.auditReport;
                    }

                    const exported = result.lessonSets.filter((item) => item.success).length;
                    const failed = result.lessonSets.length - exported;
                    const warningCount = result.warnings?.length ?? 0;
                    let message = `Export completed successfully.\n\nS3 package prefix: ${result.packagePrefix}\nExported: ${exported}\nFailed: ${failed}`;
                    if (warningCount > 0) {
                        message += `\n\nWarnings were recorded during export (${warningCount}).`;
                    }
                    await presentNativeAlert({
                        cssClass: 'custom-alert',
                        header: 'Export Content for Migration',
                        message,
                        buttons: ['OK'],
                    });
                }, 'Error exporting content.');
            } finally {
                exporting.value = false;
            }
        };

        return {
            loaded,
            auditing,
            exporting,
            categoryGroups,
            selectedCount,
            totalJourneySetCount,
            auditReport,
            exportResult,
            detailsModalOpen,
            detailsModalTitle,
            detailsModalJson,
            emptyGuid: EMPTY_GUID,
            isSelected,
            setSelection,
            selectAll,
            selectNone,
            auditSelected,
            exportSelected,
            hasAuditEntry,
            hasExportItem,
            getAuditStatusLabel,
            getAuditStatusIcon,
            getAuditStatusClass,
            getExportStatusLabel,
            getExportStatusClass,
            viewAuditDetails,
            viewExportDetails,
            downloadAuditReport,
            downloadExportResults,
            closeDetailsModal,
        };
    },
});
</script>

<style scoped>
.export-toolbar,
.export-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
}

.export-toolbar-button,
.export-row-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 8px 14px;
    border-radius: 5px;
    font-size: 14px;
    line-height: 1.2;
    width: auto;
    min-width: 0;
    max-width: none;
    flex: 1 1 calc(50% - 4px);
    cursor: pointer;
}

.export-row-button {
    flex: 0 0 auto;
    font-size: 12px;
    padding: 6px 10px;
}

.export-toolbar-button:disabled,
.export-row-button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.export-toolbar-button.button-outline,
.export-row-button.button-outline {
    background-color: var(--color-step-1000);
    color: var(--ion-color-primary);
    border: 1pt solid var(--ion-color-primary);
}

.export-toolbar-button.button-primary {
    background-color: var(--ion-color-primary);
    color: #fff;
    border: 1pt solid var(--ion-color-primary);
}

.category-header {
    margin-top: 4px;
}

.empty-label {
    color: var(--ion-color-medium);
}

.status-line {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
}

.status-icon {
    font-size: 16px;
}

.status-pass {
    color: var(--ion-color-success);
}

.status-fail {
    color: var(--ion-color-danger);
}

.status-warn {
    color: var(--ion-color-warning);
}

.row-actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
}

.details-json {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    line-height: 1.4;
}
</style>
